import { ZIndexClass } from "@/constants/z-index";
import { classNames } from "@/utils/classnames";
import React, { useCallback, useLayoutEffect, useRef, useState } from "react";

const DEFAULT_VIEWPORT_GUTTER_PX = 8;

type CSSPropertiesWithMenuVars = React.CSSProperties & {
  "--viewport-aware-fixed-menu-max-height"?: string;
};

const assignRef = <T,>(ref: React.ForwardedRef<T>, value: T | null) => {
  if (typeof ref === "function") {
    ref(value);
    return;
  }

  if (ref != null) {
    ref.current = value;
  }
};

export interface ViewportAwareFixedMenuV2Props extends React.ComponentProps<"div"> {
  left: number;
  top: number;
  anchorY?: number;
  verticalAlign?: "top" | "center";
  width?: number | string;
  height?: number | string;
  viewportGutter?: number;
}

/**
 * Positions fixed V2 menu portals while keeping them inside the viewport.
 * Consumers can use the exposed CSS var to constrain their scrollable shell:
 * `max-h-[var(--viewport-aware-fixed-menu-max-height)] overflow-y-auto`.
 */
export const ViewportAwareFixedMenuV2 = React.forwardRef<
  HTMLDivElement,
  ViewportAwareFixedMenuV2Props
>(function ViewportAwareFixedMenuV2Impl(
  {
    left,
    top,
    anchorY,
    verticalAlign = "top",
    width,
    height,
    viewportGutter = DEFAULT_VIEWPORT_GUTTER_PX,
    className,
    style,
    children,
    ...rest
  },
  forwardedRef,
) {
  const localRef = useRef<HTMLDivElement | null>(null);
  const [resolvedLeft, setResolvedLeft] = useState(left);
  const [resolvedTop, setResolvedTop] = useState(top);
  const [maxHeight, setMaxHeight] = useState<number | null>(null);

  const setRef = useCallback(
    (node: HTMLDivElement | null) => {
      localRef.current = node;
      assignRef(forwardedRef, node);
    },
    [forwardedRef],
  );

  useLayoutEffect(() => {
    if (typeof window === "undefined") return;

    const updatePosition = () => {
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const availableWidth = Math.max(0, viewportWidth - viewportGutter * 2);
      const availableHeight = Math.max(0, viewportHeight - viewportGutter * 2);
      const measuredRect = localRef.current?.getBoundingClientRect();
      const measuredWidth = measuredRect?.width ?? 0;
      const measuredHeight = measuredRect?.height ?? 0;
      const menuWidth =
        typeof width === "number"
          ? Math.min(width, availableWidth)
          : measuredWidth > 0
            ? Math.min(measuredWidth, availableWidth)
            : availableWidth;
      const menuHeight =
        typeof height === "number"
          ? Math.min(height, availableHeight)
          : measuredHeight > 0
            ? Math.min(measuredHeight, availableHeight)
            : availableHeight;
      const minLeft = viewportGutter;
      const maxLeft = Math.max(minLeft, viewportWidth - viewportGutter - menuWidth);
      const minTop = viewportGutter;
      const maxTop = Math.max(minTop, viewportHeight - viewportGutter - menuHeight);
      const rawTop = verticalAlign === "center" && anchorY != null ? anchorY - menuHeight / 2 : top;
      const nextLeft = Math.min(Math.max(left, minLeft), maxLeft);
      const nextTop = Math.min(Math.max(rawTop, minTop), maxTop);

      setResolvedLeft(nextLeft);
      setResolvedTop(nextTop);
      setMaxHeight(availableHeight);
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);

    return () => window.removeEventListener("resize", updatePosition);
  }, [anchorY, children, height, left, top, verticalAlign, viewportGutter, width]);

  const resolvedStyle: CSSPropertiesWithMenuVars = {
    ...style,
    "--viewport-aware-fixed-menu-max-height": maxHeight == null ? undefined : `${maxHeight}px`,
    left: resolvedLeft,
    top: resolvedTop,
    width,
    height,
  };

  return (
    <div
      ref={setRef}
      className={classNames("fixed", ZIndexClass.POPOVER, className)}
      style={resolvedStyle}
      {...rest}
    >
      {children}
    </div>
  );
});
